/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from 'react';
import GlobalContext from '../../../../components/Context/GloablContext';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import { 
    useGetAlertPreferencesQuery, 
    useUpdatePatientAlertPreferencesMutation
} from '../../../../features/patientSlice';

export default function AlertPreferencesSection() {
    const { setToastmessage, setToastErrorMessage } = useContext(GlobalContext);
    const { data } = useGetAlertPreferencesQuery('patient', { refetchOnMountOrArgChange: true });
    const [updatePatientAlertPreferences] = useUpdatePatientAlertPreferencesMutation();
    const [updatingField, setUpdatingField] = useState(null);
    const [optimisticUpdates, setOptimisticUpdates] = useState({});

    const handlePatientToggle = async (field, value) => {
        const fieldKey = `patient-${field}`;
        setUpdatingField(fieldKey);
        
        // Optimistic update: immediately update local state
        setOptimisticUpdates(prev => ({
            ...prev,
            [fieldKey]: value
        }));

        try {
            const response = await updatePatientAlertPreferences({ [field]: value });
            if (response?.error?.data?.message) {
                // Rollback optimistic update on error
                setOptimisticUpdates(prev => {
                    const newState = { ...prev };
                    delete newState[fieldKey];
                    return newState;
                });
                setToastErrorMessage(response.error.data.message);
            } else {
                setToastmessage('Alert preference updated successfully');
                // Don't clear optimistic update here - let useEffect handle it when cache updates
            }
        } catch (error) {
            // Rollback optimistic update on error
            setOptimisticUpdates(prev => {
                const newState = { ...prev };
                delete newState[fieldKey];
                return newState;
            });
            setToastErrorMessage('Failed to update alert preference');
        } finally {
            setUpdatingField(null);
        }
    };

    // Handle API response structure - backend now returns only patient data when type=patient
    // Could be wrapped in data property or directly as patient object
    const responseData = data?.data || data;
    const patientData = responseData?.patient || responseData || {};

    // Clear optimistic updates when server data matches the optimistic value
    useEffect(() => {
        if (patientData && Object.keys(patientData).length > 0 && Object.keys(optimisticUpdates).length > 0) {
            setOptimisticUpdates(prev => {
                const newState = { ...prev };
                let hasChanges = false;
                Object.keys(newState).forEach(key => {
                    if (key.startsWith('patient-')) {
                        const field = key.replace('patient-', '');
                        const serverValue = patientData[field] === true || patientData[field] === "true" || patientData[field] === 1;
                        const optimisticValue = newState[key];
                        // If server data matches optimistic value, clear the optimistic update
                        if (serverValue === optimisticValue) {
                            delete newState[key];
                            hasChanges = true;
                        }
                    }
                });
                return hasChanges ? newState : prev;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientData]);

    // Helper function to get toggle value (server data or optimistic update)
    const getToggleValue = (field) => {
        const optimisticKey = `patient-${field}`;
        if (optimisticUpdates.hasOwnProperty(optimisticKey)) {
            return optimisticUpdates[optimisticKey];
        }
        return patientData[field] === true || patientData[field] === "true" || patientData[field] === 1;
    };

    return (
        <>
            {/* General Alert Settings Section */}
            <div className='flex items-center my-3.5'>
                <h1 className='text-lg font-normal text-[#000000] min-w-max'>Enable Active Alerts</h1>
                <div className='relative w-full h-[1px] bg-[#008586] ml-3 down-arraow'></div>
            </div>
            <div className="flex -mx-1 flex-wrap mb-6">
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Alerts by SMS</p>
                        <ToggleSwitch
                            id="patient-sms-alert"
                            checked={getToggleValue('sms_alert')}
                            onChange={(value) => handlePatientToggle('sms_alert', value)}
                            disabled={updatingField === 'patient-sms_alert'}
                            dataTestId="patient-sms-alert-toggle"
                        />
                    </div>
                </div>
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Alerts by Email</p>
                        <ToggleSwitch
                            id="patient-email-alert"
                            checked={getToggleValue('email_alert')}
                            onChange={(value) => handlePatientToggle('email_alert', value)}
                            disabled={updatingField === 'patient-email_alert'}
                            dataTestId="patient-email-alert-toggle"
                        />
                    </div>
                </div>
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Include Missed Readings</p>
                        <ToggleSwitch
                            id="patient-missed-reading-alert"
                            checked={getToggleValue('missed_reading_alert')}
                            onChange={(value) => handlePatientToggle('missed_reading_alert', value)}
                            disabled={updatingField === 'patient-missed_reading_alert'}
                            dataTestId="patient-missed-reading-alert-toggle"
                        />
                    </div>
                </div>
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Receive Readings Acknowledgement</p>
                        <ToggleSwitch
                            id="patient-reading-received-alert"
                            checked={getToggleValue('reading_received_alert')}
                            onChange={(value) => handlePatientToggle('reading_received_alert', value)}
                            disabled={updatingField === 'patient-reading_received_alert'}
                            dataTestId="patient-reading-received-alert-toggle"
                        />
                    </div>
                </div>
            </div>

        </>
    );
}

